import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Grid,
  Paper
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import USStateSelectNative from "./rh_form_elements/rh-us-state-select-native";

const CaseInfoForm = () => {
  const { control, formState: { errors } } = useFormContext();

  return (
    <Paper elevation={1} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Case Information
      </Typography>

      <Grid container spacing={3}>
        {/*
          Phase 3b: required-field relaxation. Per-field rules now only run
          data-correctness checks (length minimums, character patterns, date
          relationships) — and even those only fire when a value is provided.
          Empty fields are no longer errors. Helper text is descriptive only;
          we don't say "required" or "optional" — the form lets you save
          either way.
        */}
        <Grid item xs={12} md={6}>
          <USStateSelectNative
              name="jurisdictionState"
              label="Jurisdiction State"
              control={control}
              helperText="State where the case is filed" />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="caseNumber"
            control={control}
            rules={{
              minLength: {
                value: 3,
                message: 'Case number must be at least 3 characters'
              }
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Case Number"
                error={!!errors.caseNumber}
                helperText={errors.caseNumber?.message}
                placeholder="12345-AB-CD"
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="courtName"
            control={control}
            rules={{
              minLength: {
                value: 5,
                message: 'Court name must be at least 5 characters'
              }
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Court Name"
                error={!!errors.courtName}
                helperText={errors.courtName?.message || "e.g., Superior Court of Cobb County"}
                placeholder="Superior Court of Cobb County"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="marriageDate"
            control={control}
            rules={{
              validate: (value) => {
                if (!value) return true; // Empty is allowed — no longer required.
                const date = new Date(value);
                const today = new Date();
                if (date > today) {
                  return 'Marriage date cannot be in the future';
                }
                if (date < new Date('1900-01-01')) {
                  return 'Marriage date must be after 1900';
                }
                return true;
              }
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Marriage Date"
                type="date"
                error={!!errors.marriageDate}
                helperText={errors.marriageDate?.message}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="separationDate"
            control={control}
            rules={{
              validate: (value, formValues) => {
                if (!value) return true;

                const separationDate = new Date(value);
                const marriageDate = new Date(formValues.marriageDate);
                const today = new Date();

                if (separationDate > today) {
                  return 'Separation date cannot be in the future';
                }

                if (formValues.marriageDate && separationDate < marriageDate) {
                  return 'Separation date must be after marriage date';
                }

                return true;
              }
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Separation Date"
                type="date"
                error={!!errors.separationDate}
                helperText={errors.separationDate?.message}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="filingDate"
            control={control}
            rules={{
              validate: (value, formValues) => {
                if (!value) return true;

                const filingDate = new Date(value);
                const today = new Date();

                if (filingDate > today) {
                  return 'Filing date cannot be in the future';
                }
                if (filingDate < new Date('1900-01-01')) {
                  return 'Filing date must be after 1900';
                }

                // Filing typically happens at or after separation, but
                // not always — some jurisdictions allow filing before
                // separation is formally established. Warn-only via
                // helper text rather than block: keep this validator
                // permissive.
                if (formValues.marriageDate) {
                  const marriageDate = new Date(formValues.marriageDate);
                  if (filingDate < marriageDate) {
                    return 'Filing date must be after marriage date';
                  }
                }

                return true;
              }
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Filing Date"
                type="date"
                error={!!errors.filingDate}
                helperText={errors.filingDate?.message || 'Date the divorce petition was filed with the court'}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            )}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default CaseInfoForm;